import { useEffect, useState } from "react";
import { getAuthTokenForRequest } from "@/lib/api";

/**
 * Per-note / per-clip thumbnail loader.
 *
 * The backend thumbnail endpoints —
 *   `GET /api/coach/notes/{id}/thumbnail`
 *   `GET /api/coach/clips/{id}/thumbnail`
 * — are access-controlled per viewer. An `<img src>` can't attach
 * fallback bearer auth, so the browser renders them as: `fetch` →
 * `blob()` → `URL.createObjectURL`. This module owns that primitive
 * plus a shared cache so a list of 30 chips doesn't re-fetch the
 * same blob on every re-render.
 *
 * Ported from legacy `js/api.js`'s `loadCoachNoteThumbnail` /
 * `mountCoachNoteThumbnailsIn` pair. Reused by the timeline chip
 * rail, the clip composer's "From note #N" context, and the
 * focused feedback player modal — built as one primitive so the
 * fetch/cache/revoke logic isn't half-implemented in three places.
 *
 * **Why raw `fetch`, not `apiFetch`:** thumbnails fan out — one
 * fetch per visible row. Routing every one through the shared
 * fetch wrapper would let a single expired token spam dozens of
 * parallel auth-failure handlers. Raw `fetch` lets every 401 /
 * 403 / 404 degrade silently to the placeholder; the user's next
 * real action surfaces the auth flow properly.
 *
 * **Cache layout** — `Map<"note:42" | "clip:7", Entry>`:
 *   - `Promise<string | null>` — in-flight fetch (dedupes
 *     concurrent calls for the same id)
 *   - `{ url: string }` — resolved, object URL ready
 *   - `{ url: null }` — negative cache (404 / 403 / network) so a
 *     long rail of thumbnail-less notes doesn't re-fire forever
 *
 * **Generation counter** — `revokeAllThumbnails()` (called on the
 * auth boundary, see `useMe`) bumps the generation, revokes every
 * cached object URL, and clears the map. In-flight fetches capture
 * the generation at start and discard their result (revoking the
 * fresh blob) if the generation moved — so a logout mid-fetch
 * can't leak a prior-session blob into the next session's cache.
 */

export type ThumbnailKind = "note" | "clip";

type CacheEntry =
  | Promise<string | null>
  | { url: string }
  | { url: null };

const cache = new Map<string, CacheEntry>();
let generation = 0;
let abortController = new AbortController();

function cacheKey(kind: ThumbnailKind, id: number): string {
  return `${kind}:${id}`;
}

function endpoint(kind: ThumbnailKind, id: number): string {
  return kind === "note"
    ? `/api/coach/notes/${id}/thumbnail`
    : `/api/coach/clips/${id}/thumbnail`;
}

/**
 * Revoke every cached thumbnail object URL and clear the cache.
 * Call this on the auth boundary (sign-out / 401 from `/api/me`) —
 * blobs from a prior session must not outlive their visibility
 * context. Bumps the generation so in-flight fetches discard their
 * results, and aborts them so they don't run to completion against
 * a now-stale token.
 */
export function revokeAllThumbnails(): void {
  generation += 1;
  abortController.abort();
  abortController = new AbortController();
  for (const entry of cache.values()) {
    if (entry && "url" in entry && entry.url) {
      try {
        URL.revokeObjectURL(entry.url);
      } catch {
        // already revoked / not an object URL — ignore
      }
    }
  }
  cache.clear();
}

/**
 * Drop one thumbnail's cache entry (positive or negative) so the
 * next load re-fetches. Call after a successful regenerate so the
 * refreshed JPEG appears. Revokes the old object URL if present.
 */
export function invalidateThumbnail(kind: ThumbnailKind, id: number): void {
  const key = cacheKey(kind, id);
  const entry = cache.get(key);
  if (entry && "url" in entry && entry.url) {
    try {
      URL.revokeObjectURL(entry.url);
    } catch {
      // ignore
    }
  }
  cache.delete(key);
}

/**
 * Load a thumbnail's object URL. Returns `null` on any failure
 * (no thumbnail yet / viewer can't see it / network error) — the
 * caller renders a placeholder. Never throws. Concurrent calls for
 * the same id share one fetch.
 */
export async function loadThumbnail(
  kind: ThumbnailKind,
  id: number,
): Promise<string | null> {
  if (!Number.isFinite(id) || id <= 0) return null;
  const key = cacheKey(kind, id);
  const cached = cache.get(key);
  if (cached !== undefined) {
    if (cached instanceof Promise) return cached;
    return cached.url;
  }

  const startGen = generation;
  const { signal } = abortController;
  const promise = (async (): Promise<string | null> => {
    try {
      const token = getAuthTokenForRequest();
      const resp = await fetch(endpoint(kind, id), {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        signal,
      });
      // Logout / cache-clear landed mid-fetch — discard, don't
      // write to the (now-cleared) cache.
      if (generation !== startGen) return null;
      if (!resp.ok) {
        // 404 / 403 / 5xx → negative-cache so the rail doesn't
        // re-fire every render.
        cache.set(key, { url: null });
        return null;
      }
      const blob = await resp.blob();
      if (generation !== startGen) {
        // Logout landed while the body streamed — nothing cached
        // yet, nothing to revoke (no object URL created).
        return null;
      }
      const url = URL.createObjectURL(blob);
      // Final guard between createObjectURL and the cache write.
      if (generation !== startGen) {
        try {
          URL.revokeObjectURL(url);
        } catch {
          // ignore
        }
        return null;
      }
      cache.set(key, { url });
      return url;
    } catch {
      // AbortError (logout) or network failure. Only negative-
      // cache a genuine network error — an abort means the cache
      // was already cleared, so writing would resurrect a stale
      // key. `generation` moving is the abort signal.
      if (generation === startGen) {
        cache.set(key, { url: null });
      }
      return null;
    }
  })();

  cache.set(key, promise);
  return promise;
}

/**
 * React hook — resolves a thumbnail object URL for one note/clip.
 * Returns `null` until the fetch lands AND on any failure (the
 * caller shows a placeholder either way). Re-runs when `id` or
 * `kind` changes; ignores a resolution that lands after the
 * component moved to a different id.
 *
 * `enabled` (#210) — pass `false` to skip the GET entirely. The
 * Feedback Library / Notes list uses `note.has_thumbnail` from the
 * /api/coach/notes response to gate this: 6+ failed GETs per page
 * load were cluttering the console because the FE fired one per
 * note unconditionally. Defaults to `true` for callers that always
 * want to try (e.g. the focused player modal where we know a clip
 * exists). When `false`, the hook returns null without touching the
 * cache, so a later `enabled: true` re-render still gets a fresh
 * attempt rather than a stale negative-cached entry.
 */
export function useThumbnail(
  kind: ThumbnailKind,
  id: number | null | undefined,
  enabled: boolean = true,
): string | null {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || id == null || !Number.isFinite(id) || id <= 0) {
      setUrl(null);
      return;
    }
    let active = true;
    setUrl(null);
    loadThumbnail(kind, id).then((resolved) => {
      if (active) setUrl(resolved);
    });
    return () => {
      active = false;
    };
  }, [kind, id, enabled]);

  return url;
}
